import React from 'react'
import {
  TextField, Button, Stack, Paper, Typography,
  MenuItem, Select, FormControl, InputLabel, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormHelperText
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'

interface Company {
  id: number
  name: string
}

interface MemberEntry {
  id?: number
  name: string
  stage_name: string
  _destroy?: boolean
}

interface FormData {
  name: string
  company_id: string
  gender: string
  group_type: string
  description: string
  members_attributes: MemberEntry[]
}

interface Props {
  data: FormData
  setData: (key: string, value: any) => void
  errors: Record<string, string[]>
  companies: Company[]
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
}

export default function GroupForm({ data, setData, errors, companies, processing, onSubmit, submitLabel }: Props) {
  const addMember = () => {
    setData('members_attributes', [
      ...data.members_attributes,
      { name: '', stage_name: '' }
    ])
  }

  const updateMember = (index: number, field: string, value: string) => {
    const updated = data.members_attributes.map((m, i) =>
      i === index ? { ...m, [field]: value } : m
    )
    setData('members_attributes', updated)
  }

  const removeMember = (index: number) => {
    const member = data.members_attributes[index]
    if (member.id) {
      const updated = data.members_attributes.map((m, i) =>
        i === index ? { ...m, _destroy: true } : m
      )
      setData('members_attributes', updated)
    } else {
      const updated = data.members_attributes.filter((_, i) => i !== index)
      setData('members_attributes', updated)
    }
  }

  const visibleMembers = data.members_attributes
    .map((m, i) => ({ ...m, _index: i }))
    .filter((m) => !m._destroy)

  return (
    <form onSubmit={onSubmit}>
      <Paper sx={{ p: 4, mb: 4 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Name"
            value={data.name}
            onChange={(e) => setData('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name?.[0]}
            required
            fullWidth
          />

          <FormControl fullWidth error={!!errors.company_id}>
            <InputLabel>Company</InputLabel>
            <Select
              value={data.company_id}
              label="Company"
              onChange={(e) => setData('company_id', e.target.value)}
            >
              {companies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
              ))}
            </Select>
            {errors.company_id && <FormHelperText>{errors.company_id[0]}</FormHelperText>}
          </FormControl>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth error={!!errors.gender}>
              <InputLabel>Gender</InputLabel>
              <Select
                value={data.gender}
                label="Gender"
                onChange={(e) => setData('gender', e.target.value)}
              >
                <MenuItem value="girl">Girl</MenuItem>
                <MenuItem value="boy">Boy</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
              </Select>
              {errors.gender && <FormHelperText>{errors.gender[0]}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth error={!!errors.group_type}>
              <InputLabel>Type</InputLabel>
              <Select
                value={data.group_type}
                label="Type"
                onChange={(e) => setData('group_type', e.target.value)}
              >
                <MenuItem value="group">Group</MenuItem>
                <MenuItem value="band">Band</MenuItem>
              </Select>
              {errors.group_type && <FormHelperText>{errors.group_type[0]}</FormHelperText>}
            </FormControl>
          </Stack>

          <TextField
            label="Description"
            value={data.description}
            onChange={(e) => setData('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
      </Paper>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Members</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={addMember}
            variant="outlined"
          >
            Add Member
          </Button>
        </Stack>

        {visibleMembers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No members yet. Click "Add Member" to add one.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', width: 60 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleMembers.map((member) => (
                  <TableRow key={member._index}>
                    <TableCell>
                      <TextField
                        value={member.name}
                        onChange={(e) => updateMember(member._index, 'name', e.target.value)}
                        placeholder="Full name"
                        size="small"
                        fullWidth
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={member.stage_name}
                        onChange={(e) => updateMember(member._index, 'stage_name', e.target.value)}
                        placeholder="Stage name"
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => removeMember(member._index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Button
        type="submit"
        variant="contained"
        disabled={processing}
      >
        {submitLabel}
      </Button>
    </form>
  )
}
